import Cookies from "js-cookie";
import { ArrowLeft, Plus } from "lucide-react";
import React from "react";
import { useLocation, useNavigate, useParams } from "react-router-dom";

import { Button, PermissionGuard } from "@/components";
import { IAuthenticated } from "@/context";
import { useI18n } from "@/context/i18n";

import { useClients } from "../clients/clients.hooks";
import { PlatformsTable } from "./components";
import { usePlataform } from "./platform.hooks";
import { TPlatform } from "./platform.types";
import { PlataformForm } from "./platform-form";

export const PlatformPage: React.FC = () => {
  const { t } = useI18n();
  const { clientId, projectId } = useParams<{ clientId: string; projectId?: string }>();
  const navigate = useNavigate();
  const location = useLocation();
  const state = location.state as {
    fromClients?: boolean;
    createNew?: boolean;
    returnPath?: string;
    openEditPlatform?: TPlatform;
  } | null;
  const isFromClients = Boolean(state?.fromClients);
  const shouldCreateNew = Boolean(state?.createNew);
  const returnPath = state?.returnPath;

  const { client, isLoading } = useClients(clientId);

  const userData: IAuthenticated = JSON.parse(Cookies.get("authUser") || "{}");

  const isOrganizationRoot = userData.organization_root_id === clientId;
  
  // Debug log
  React.useEffect(() => {
    console.log("Platform page state:", location.state);
    console.log("isFromClients:", isFromClients);
    console.log("isOrganizationRoot:", isOrganizationRoot);
  }, [location.state, isFromClients, isOrganizationRoot]);

  const plataformHook = usePlataform();
  const {
    openPlataformForm,
    isPlataformFormOpen,
    setIsPlataformFormOpen,
    plataformForm,
    platforms,
    isLoadingPlatforms,
    fetchPlatforms,
    createPlatformCallback,
    isCreatingPlatform,
    openEditPlatform,
    closeEditPlatform,
    isEditMode,
    editingPlatform,
    updatePlatformCallback,
    isUpdatingPlatform,
    deletePlatformCallback,
    isDeletingPlatform,
  } = plataformHook;

  const lastOpenedEditPlatformIdRef = React.useRef<string | null>(null);

  React.useEffect(() => {
    const platformToEdit = state?.openEditPlatform;
    if (!platformToEdit?.id) {
      lastOpenedEditPlatformIdRef.current = null;
      return;
    }
    if (lastOpenedEditPlatformIdRef.current === platformToEdit.id) {
      return;
    }
    lastOpenedEditPlatformIdRef.current = platformToEdit.id;
    openEditPlatform(platformToEdit);
    navigate(`${location.pathname}${location.search}`, {
      replace: true,
      state: {
        fromClients: state?.fromClients,
        createNew: state?.createNew,
        returnPath: state?.returnPath,
      },
    });
  }, [
    state?.openEditPlatform,
    openEditPlatform,
    navigate,
    location.pathname,
    location.search,
    state?.fromClients,
    state?.createNew,
    state?.returnPath,
  ]);

  React.useEffect(() => {
    // Sempre precisa do clientId (organizationId) para buscar plataformas
    // Se tiver projectId, será passado como query parameter
    if (client?.id) {
      fetchPlatforms(client.id, projectId);
    }
  }, [client?.id, projectId, fetchPlatforms]);

  // Abrir formulário automaticamente se vier da tela de detalhes do projeto para criar nova landing page
  React.useEffect(() => {
    if (shouldCreateNew && client?.id && !isPlataformFormOpen) {
      openPlataformForm(client.id);
    }
  }, [shouldCreateNew, client?.id, isPlataformFormOpen, openPlataformForm]);

  const handleDeletePlatform = React.useCallback(
    async (platformId: string) => {
      if (!client?.id) return;
      
      try {
        await deletePlatformCallback(client.id, platformId);
      } catch (error) {
        console.error("Erro ao deletar plataforma:", error);
      }
    },
    [client?.id, deletePlatformCallback]
  );

  return (
    <div className="flex flex-col w-full h-full space-y-6 p-4">
      <div className="flex flex-row items-start justify-between">
        <div className="flex flex-col w-full px-2">
          <div className="flex items-center gap-3 mb-3">
            {(isFromClients || isPlataformFormOpen || returnPath) && (
              <Button
                type="button"
                variant="ghost"
                onClick={() => {
                  if (isPlataformFormOpen) {
                    closeEditPlatform();
                    // Se fechar o formulário e houver returnPath, voltar para a página anterior
                    if (returnPath) {
                      setTimeout(() => {
                        navigate(returnPath);
                      }, 100);
                    }
                  } else if (returnPath) {
                    navigate(returnPath);
                  } else {
                    navigate("/clients");
                  }
                }}
                size="sm"
                className="p-3 hover:bg-muted/50 transition-colors"
              >
                <ArrowLeft className="h-5 w-5" />
              </Button>
            )}
            <h1 className="text-3xl font-bold tracking-tight text-foreground">
              {isOrganizationRoot
                ? t("platform.title")
                : `Plataformas: ${client?.name}`}
            </h1>
          </div>
          
          {isPlataformFormOpen && isEditMode && editingPlatform?.landing_page?.title ? (
            <div className="space-y-1">
              <div className="flex items-center gap-2">
                <span className="text-sm font-medium text-muted-foreground">Parceiro:</span>
                <span className="text-sm font-semibold text-foreground">{client?.name}</span>
              </div>
              <div className="flex items-center gap-2">
                <span className="text-sm font-medium text-muted-foreground">Editando a plataforma:</span>
                <span className="text-sm font-semibold text-primary">{editingPlatform.landing_page.title}</span>
              </div>
            </div>
          ) : isOrganizationRoot ? (
            <p className="text-sm text-muted-foreground leading-relaxed">
              {t("platform.description")}
            </p>
          ) : client?.name ? (
            <div className="flex items-center gap-2">
              <span className="text-sm font-medium text-muted-foreground">Parceiro:</span>
              <span className="text-sm font-semibold text-foreground">{client.name}</span>
            </div>
          ) : null}
        </div>
        
        {!isPlataformFormOpen && (
          <div className="flex justify-end">
            <PermissionGuard permission="platform:create">
              <Button 
                onClick={() => openPlataformForm(clientId || "")}
                className="shadow-sm hover:shadow-md transition-shadow"
              >
                <Plus className="mr-2 h-4 w-4" />
                {"Nova plataforma"}
              </Button>
            </PermissionGuard>
          </div>
        )}
      </div>
      {!isPlataformFormOpen && (
        <>
          {isLoading ? (
            <div className="flex items-center justify-center py-8">
              <div className="text-center">
                <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary mx-auto mb-4"></div>
                <p className="text-muted-foreground">
                  Carregando dados do cliente...
                </p>
              </div>
            </div>
          ) : (
            <div className="space-y-6">
              {/* Tabela de Plataformas */}
              <PlatformsTable
                platforms={platforms}
                isLoading={isLoadingPlatforms}
                onEditPlatform={openEditPlatform}
                onDeletePlatform={handleDeletePlatform}
                isDeleting={isDeletingPlatform}
              />
            </div>
          )}
        </>
      )}

      {isPlataformFormOpen && (
        <PlataformForm
          open={isPlataformFormOpen}
          form={plataformForm}
          onSubmit={isEditMode ? 
            async (formData) => {
              // Incluir project_id se disponível
              const formDataWithProject = projectId ? { ...formData, project_id: projectId } : formData;
              await updatePlatformCallback(client?.id || "", editingPlatform?.id || "", formDataWithProject);
              // Recarregar plataformas após atualizar
              if (client?.id) {
                fetchPlatforms(client.id, projectId);
              }
              // Fechar formulário e voltar se houver returnPath
              setIsPlataformFormOpen(false);
              if (returnPath) {
                navigate(returnPath);
              }
            } :
            async (formData) => {
              // Incluir project_id se disponível
              const formDataWithProject = projectId ? { ...formData, project_id: projectId } : formData;
              await createPlatformCallback(client?.id || "", formDataWithProject);
              // Recarregar plataformas após criar
              if (client?.id) {
                fetchPlatforms(client.id, projectId);
              }
              // Fechar formulário e voltar se houver returnPath
              setIsPlataformFormOpen(false);
              if (returnPath) {
                navigate(returnPath);
              }
            }
          }
          clientId={clientId || ""}
          projectId={projectId}
          isEditMode={isEditMode}
          isLoading={isEditMode ? isUpdatingPlatform : isCreatingPlatform}
          plataformHook={plataformHook}
        />
      )}
    </div>
  );
};
